import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { 
  Mountain, FileText, AlertTriangle, Users, LogOut, Building2,
  ClipboardCheck, Map, Settings, BarChart3, Bell, MessageSquare,
  Calendar, CreditCard, Scale, HardHat, FileCheck, Zap, Mail,
  LayoutDashboard, HelpCircle, Activity, Download, ChevronLeft,
  ChevronRight, Menu, X, Gavel, Shield, Home, FolderOpen,
  Hammer, Megaphone, Heart, UserCog, Database, Bot, Search, Camera
} from "lucide-react";
import UnifiedSearchBar from "./UnifiedSearchBar";

const AdminSidebar = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role || "owner";
  const isAdmin = role === "admin";
  const isManagement = ["admin", "board", "property_manager"].includes(role);
  const isDRC = ["admin", "board", "drc"].includes(role);
  const isAccountant = ["admin", "accountant"].includes(role);

  // ROLE-BASED NAVIGATION per Redesign Strategy
  // Homeowner: 5 items | Board: 8 items | Admin: Board + Settings
  const getNavSections = () => {
    if (role === "owner") {
      return [
        { title: null, items: [
          { icon: <Home className="w-5 h-5" />, label: "Home", href: "/owner" },
          { icon: <FolderOpen className="w-5 h-5" />, label: "Documents", href: "/board/documents" },
          { icon: <Hammer className="w-5 h-5" />, label: "My Projects", href: "/board/drc" },
          { icon: <Heart className="w-5 h-5" />, label: "Community", href: "/board/forum" },
          { icon: <UserCog className="w-5 h-5" />, label: "My Account", href: "/owner/profile" },
        ]}
      ];
    }

    if (role === "accountant") {
      return [
        { title: null, items: [
          { icon: <BarChart3 className="w-5 h-5" />, label: "Dashboard", href: "/accountant" },
          { icon: <CreditCard className="w-5 h-5" />, label: "Payments", href: "/board/payments" },
          { icon: <CreditCard className="w-5 h-5" />, label: "Budgets", href: "/board/budgets" },
          { icon: <Building2 className="w-5 h-5" />, label: "Vendors", href: "/board/vendors" },
        ]}
      ];
    }

    // Board / Property Manager / DRC / Admin
    const sections = [
      { title: null, items: [
        { icon: <BarChart3 className="w-5 h-5" />, label: "Dashboard", href: "/board" },
      ]},
      { title: "Core", items: [
        { icon: <FolderOpen className="w-5 h-5" />, label: "Documents", href: "/board/documents" },
        { icon: <Gavel className="w-5 h-5" />, label: "Enforcement", href: "/board/enforcement" },
        { icon: <ClipboardCheck className="w-5 h-5" />, label: "Design Review", href: "/board/drc" },
        { icon: <Megaphone className="w-5 h-5" />, label: "Communications", href: "/board/communications-hub" },
      ]},
      { title: "Directory", items: [
        { icon: <Building2 className="w-5 h-5" />, label: "Properties & Owners", href: "/board/properties" },
        { icon: <Map className="w-5 h-5" />, label: "Map", href: "/board/gis" },
      ]},
      { title: "Operations", items: [
        { icon: <Calendar className="w-5 h-5" />, label: "Meetings", href: "/board/meetings" },
        { icon: <CreditCard className="w-5 h-5" />, label: "Payments", href: "/board/payments" },
        { icon: <Building2 className="w-5 h-5" />, label: "Vendors", href: "/board/vendors" },
        { icon: <Heart className="w-5 h-5" />, label: "Community", href: "/board/forum" },
        { icon: <Camera className="w-5 h-5" />, label: "Community Reports", href: "/board/community-reports" },
      ]},
    ];

    // Admin gets settings section
    if (isAdmin) {
      sections.push({
        title: "Admin",
        items: [
          { icon: <Shield className="w-5 h-5" />, label: "Admin Panel", href: "/admin" },
          { icon: <Settings className="w-5 h-5" />, label: "Integrations", href: "/admin/integrations" },
          { icon: <FileText className="w-5 h-5" />, label: "Governance Notices", href: "/admin/governance-notices" },
          { icon: <Users className="w-5 h-5" />, label: "Owner Verification", href: "/admin/owner-verification" },
          { icon: <BarChart3 className="w-5 h-5" />, label: "Analytics", href: "/admin/complaints/analytics" },
          { icon: <Download className="w-5 h-5" />, label: "Export & Logs", href: "/admin/export" },
          { icon: <Database className="w-5 h-5" />, label: "Backups", href: "/admin/backups" },
        ]
      });
    }

    return sections;
  };

  const navSections = getNavSections();

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.href || 
                     (item.href !== "/board" && item.href !== "/owner" && location.pathname.startsWith(item.href));
    
    return (
      <Link to={item.href} onClick={() => setMobileOpen(false)}>
        <Button
          variant={isActive ? "secondary" : "ghost"}
          size="sm"
          className={`w-full justify-start gap-3 ${
            isActive 
              ? "bg-primary/10 text-primary font-medium" 
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          }`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {item.icon}
          {!collapsed && <span className="truncate">{item.label}</span>}
        </Button>
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`p-4 border-b flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        <Link to={role === "owner" ? "/owner" : "/board"} className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Mountain className="w-6 h-6 text-primary" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="font-serif font-semibold text-sm truncate">Horse Mountain Ranch</p>
              <Badge variant="secondary" className="text-xs mt-0.5 bg-primary/20">
                {role === "property_manager" ? "PM" : role?.toUpperCase()}
              </Badge>
            </div>
          )}
        </Link>
        {!collapsed && (
          <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setCollapsed(true)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Search Bar */}
      {!collapsed && (
        <div className="px-3 py-2 border-b">
          <UnifiedSearchBar />
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <div className="space-y-5">
          {navSections.map((section, idx) => (
            <div key={idx}>
              {!collapsed && section.title && (
                <p className="text-xs font-semibold text-muted-foreground px-2 mb-2 uppercase tracking-wider">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item, itemIdx) => (
                  <NavItem key={itemIdx} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* User & Logout */}
      <div className="border-t p-4">
        {!collapsed && (
          <div className="mb-3">
            <p className="font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        )}
        <Button
          variant="ghost" size="sm" onClick={logout}
          className={`text-muted-foreground hover:text-foreground ${collapsed ? 'w-full justify-center' : 'w-full justify-start'}`}
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4" />
          {!collapsed && <span className="ml-2">Sign Out</span>}
        </Button>
        {collapsed && (
          <Button variant="ghost" size="icon" className="w-full mt-2" onClick={() => setCollapsed(false)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-card border-r transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'}`}>
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b px-4 py-3 flex items-center gap-3">
        <Link to={role === "owner" ? "/owner" : "/board"} className="flex items-center gap-2 shrink-0">
          <Mountain className="w-6 h-6 text-primary" />
        </Link>
        <div className="flex-1">
          <UnifiedSearchBar />
        </div>
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" className="shrink-0">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 bg-card transform transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 mt-[57px] lg:mt-0 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default AdminSidebar;
